import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMe } from "@/hooks/queries/use-me";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { adoItemsPageName, adoSetupRedirect, isToolboxPatPage } from "@/lib/toolbox/page-names";

interface RequireAdoPatProps {
  children: ReactNode;
}

/**
 * Guard for every Azure DevOps page (My Items, item detail, the grooming
 * pages and the Toolbox Formulator). Waits for GET /me, then, when the user
 * has no ADO PAT saved:
 * - on a SpecTree Toolbox page (the grooming routes and /toolbox/formulator),
 *   sends them to Setup's "Connect Azure DevOps" step with the page's name
 *   and path (`/toolbox/setup?step=azure-devops&from=…&page=…`), so Setup can
 *   say why and return them after a pass (epic PI-SPEC-SW-E288, frame 1c,
 *   03-evaluation.md section 8.6 Q5: the PM never lands on SpecTree Settings);
 * - on SpecTree's own ADO pages (/ado/items), redirects to Settings in a
 *   browser, the same behaviour as the FogBugz pages
 *   (`pages/fogbugz/formulator.tsx`); inside SpecTree Toolbox they go to the
 *   same Setup step as "My ADO Items", as the palette sends people there
 *   (PKG-PM-D Mac walk, 2026-10-23).
 *
 * Saving a PAT invalidates the `me` query, so the ADO pages and nav item
 * become available without a reload.
 */
export function RequireAdoPat({ children }: RequireAdoPatProps) {
  const { data: meData, isLoading } = useMe();
  const location = useLocation();
  const bridge = useToolboxBridge();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <div className="text-center space-y-3">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!meData?.hasAdoPat) {
    if (isToolboxPatPage(location.pathname) || (bridge !== null && adoItemsPageName(location.pathname) !== null)) {
      return <Navigate to={adoSetupRedirect(location.pathname, location.search)} replace />;
    }
    return <Navigate to="/settings" replace />;
  }

  return <>{children}</>;
}
